import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useState, type ComponentProps, type ReactNode } from "react";

/**
 * The Artifact panel: a fixed frame (header + actions + isolated preview)
 * around whatever HTML document the model wrote, modelled on
 * `web-preview.tsx`'s composition (root + sub-components, one concern each).
 *
 * Written by hand rather than pulled from the `ai-elements` registry (D-10):
 * this environment's outbound network access is not guaranteed, and
 * `web-preview.tsx` — already in this repo, already reviewed — is the
 * faithful structural reference the ticket itself points to.
 *
 * Unlike `WebPreview`, this component carries no `sandbox="allow-same-origin"`
 * anywhere: the document it renders was written by a model, never by Shaarp,
 * and the isolation that makes that safe (sandbox flags, a MinIO origin
 * distinct from the app, a CSP baked into the stored document) is this
 * ticket's whole security model (`docs/rules/security.md`). See
 * `chat-artifact-panel.tsx` for where the iframe itself is rendered.
 */
export type ArtifactProps = ComponentProps<"div">;

export function Artifact({ className, children, ...props }: ArtifactProps) {
  return (
    <div
      className={cn(
        "flex size-full flex-col border-l bg-background",
        className,
      )}
      data-slot="artifact"
      {...props}
    >
      {children}
    </div>
  );
}

export type ArtifactHeaderProps = ComponentProps<"div">;

export function ArtifactHeader({
  className,
  children,
  ...props
}: ArtifactHeaderProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 border-b bg-background px-3 py-2",
        className,
      )}
      data-slot="artifact-header"
      {...props}
    >
      {children}
    </div>
  );
}

export type ArtifactTitleProps = ComponentProps<"span">;

export function ArtifactTitle({
  className,
  children,
  ...props
}: ArtifactTitleProps) {
  return (
    <span
      className={cn("min-w-0 flex-1 truncate text-sm font-medium", className)}
      data-slot="artifact-title"
      {...props}
    >
      {children}
    </span>
  );
}

export type ArtifactActionsProps = ComponentProps<"div">;

export function ArtifactActions({
  className,
  children,
  ...props
}: ArtifactActionsProps) {
  return (
    <div
      className={cn("flex shrink-0 items-center gap-1", className)}
      data-slot="artifact-actions"
      {...props}
    >
      {children}
    </div>
  );
}

export type ArtifactActionProps = ComponentProps<typeof Button> & {
  tooltip?: ReactNode;
};

export function ArtifactAction({
  tooltip,
  children,
  className,
  ...props
}: ArtifactActionProps) {
  const button = (
    <Button
      className={cn("size-8 p-0 hover:text-foreground", className)}
      data-slot="artifact-action"
      size="sm"
      variant="ghost"
      {...props}
    >
      {children}
    </Button>
  );

  if (!tooltip) {
    return button;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export type ArtifactContentProps = ComponentProps<"iframe"> & {
  fallback?: ReactNode;
  /** No `src` yet because it is still being fetched: the page placeholder, not `fallback`. */
  isPending?: boolean;
};

/**
 * The isolated preview itself.
 *
 * `sandbox=""` is the strictest value there is: no script, no same origin,
 * no popup, no form, no top-level navigation. The document was written by a
 * model — possibly steered by a page it read (`docs/decisions/0006`) — so it
 * is sanitized to static HTML before storage (`wrapArtifactHtml`, API side)
 * and served from a MinIO origin distinct from the app; this is the second
 * lock on the same door. Printing happens from a plain new tab, never
 * through this frame — see D-05 in the PR description.
 */
export function ArtifactContent({
  className,
  fallback,
  isPending = false,
  src,
  onLoad,
  ...props
}: ArtifactContentProps) {
  // Tied to the URL it loaded rather than a plain flag, so switching
  // Artifacts shows the placeholder again instead of the previous frame.
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const isLoaded = !!src && loadedSrc === src;

  if (!src && isPending) {
    return (
      <div className="relative flex min-h-0 flex-1" data-slot="artifact-content">
        <ArtifactLoading />
      </div>
    );
  }

  if (!src) {
    return (
      <div
        className="flex flex-1 items-center justify-center text-sm text-muted-foreground"
        data-slot="artifact-content"
      >
        {fallback}
      </div>
    );
  }

  // The frame stays mounted while hidden: it has to load to fire `onLoad`.
  // Faded in rather than shown bare, so the user never sees the white,
  // half-painted page a cross-origin document renders while it arrives.
  return (
    <div className="relative flex min-h-0 flex-1">
      {!isLoaded && <ArtifactLoading />}
      <iframe
        // `color-scheme` from the app's theme, not the OS: inside the frame,
        // `prefers-color-scheme` reports this value, and the stored document
        // switches its colour tokens on it (`wrapArtifactHtml`, API side).
        className={cn(
          "size-full flex-1 border-0 transition-opacity duration-300 ease-out [color-scheme:light] dark:[color-scheme:dark]",
          isLoaded ? "opacity-100" : "opacity-0",
          className,
        )}
        data-slot="artifact-content"
        data-state={isLoaded ? "loaded" : "loading"}
        onLoad={(event) => {
          setLoadedSrc(src);
          onLoad?.(event);
        }}
        sandbox=""
        src={src}
        title="Artifact"
        {...props}
      />
    </div>
  );
}

/** A page-shaped placeholder: a title, then a few lines of body text. */
function ArtifactLoading() {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 flex flex-col gap-3 p-6"
      data-slot="artifact-loading"
    >
      <Skeleton className="mb-2 h-6 w-2/5" />
      <Skeleton className="h-3.5 w-full" />
      <Skeleton className="h-3.5 w-11/12" />
      <Skeleton className="h-3.5 w-4/5" />
      <Skeleton className="mt-4 h-3.5 w-full" />
      <Skeleton className="h-3.5 w-3/5" />
    </div>
  );
}
